import React, { ReactNode, useState } from 'react'
import * as R from 'remeda'
import { createContextWithHook } from '@chilipiper/utils'
import { observableResource, SubscribeCallback } from '../chat-data/observableResource'
import { RoomEventsMap, RoomsMap } from '../chat-data/types'
import { ClientEvents, useXMPPClient } from './useXMPPClient'

type Client = ReturnType<typeof useXMPPClient>

const getClientMock = ({ events = {} }: { events?: RoomEventsMap }): Client => ({
  start: async (_tenantId, _workspaceId, start) => {
    await start(false)
  },
  userId: 'userId',
  value: { events },
  stop: () => Promise.resolve(),
  loadInbox: () => Promise.resolve([]),
  loadRoomDetails: () => Promise.resolve(undefined),
  loadRoomEvents: () => Promise.resolve([]),
  sendEvent: () => Promise.resolve('id'),
  markEvent: () => Promise.resolve(),
  status: 'CONNECTED',
  setEvent: R.doNothing,
  roomAssignees: {},
  loadAffiliations: () => Promise.resolve(),
  setMuteStatus: () => Promise.reject(Error('not implemented')),
})
const getMockedContextValue = (options: { events?: RoomEventsMap; roomMap?: RoomsMap }) => ({
  client: getClientMock(options),
  publicClient: getClientMock(options),
  subscribe: () => R.doNothing,
})

const { XMPPClientBaseProvider, useXMPPClientContext } = createContextWithHook<{
  client: Client
  publicClient: Client
  subscribe: SubscribeCallback<ClientEvents>
}>()('XMPPClient')

export { useXMPPClientContext }

export const MockXMPPClientProvider = ({
  children,
  events,
  roomMap,
  interceptClients = clients => clients,
}: {
  children: ReactNode
  events?: RoomEventsMap
  interceptClients?: (clients: {
    client: Client
    publicClient: Client
    subscribe: SubscribeCallback<ClientEvents>
  }) => {
    client: Client
    publicClient: Client
    subscribe: SubscribeCallback<ClientEvents>
  }
  roomMap?: RoomsMap
}) => {
  return (
    <XMPPClientBaseProvider value={interceptClients(getMockedContextValue({ events, roomMap }))}>
      {children}
    </XMPPClientBaseProvider>
  )
}

export const XMPPClientProvider = ({ children }: { children?: ReactNode }) => {
  const [eventsStream] = useState(() => observableResource<ClientEvents>(['event', 'roomRead']))
  const client = useXMPPClient('private', eventsStream.publish)
  const publicClient = useXMPPClient('public', eventsStream.publish)

  return (
    <XMPPClientBaseProvider value={{ client, publicClient, subscribe: eventsStream.subscribe }}>
      {children}
    </XMPPClientBaseProvider>
  )
}
